import { FolderOutlined } from '@ant-design/icons';

import ProfileAvatar from '@/components/Avatar/ProfileAvatar';
import { CenterFlex, Flex, VFlex } from '@/components/base';
import FavoriteToggle from '@/components/Common/FavoriteToggle';
import { ContentTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import type {
  Dashboard2FolderT,
  Dashboard2ListT,
  Dashboard2T,
} from '@/features/Dashboard/types/types';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { getTimezoneDayjs } from '@/utils/time';

import DashboardIcon from '../../../assets/icons/DashboardIcon';
import Dashboard2Action from '../components/DashboardAction';
import DashboardFolderAction from '../components/DashboardFolderAction';
import {
  useDashboardStarMutation,
  useDashboardTreeListQuery,
} from './useDashboardListQuery';

const columns: DataTableColumnT[] = [
  {
    columnKey: 'title',
    columnName: 'Name',
    visible: true,
    responsive: true,
    isDefaultColumn: true,
  },
  {
    columnKey: 'last_updated',
    columnName: 'Last updated',
    width: 160,
    visible: true,
    isDefaultColumn: true,
  },
  {
    columnKey: 'modifed_by',
    columnName: 'Modified by',
    width: 160,
    visible: true,
    isDefaultColumn: true,
  },
  {
    columnKey: 'actions',
    columnName: 'Actions',
    width: 60,
    visible: true,
    isDefaultColumn: true,
  },
];

const useDashboard2ListTable = ({
  searchValue,
  onEditDashboard,
  onEditDashboardFolder,
  data,
  refetch,
  folderAlias,
}: {
  searchValue: string | null;
  onEditDashboard: (dashboard: Dashboard2T) => void;
  onEditDashboardFolder?: (dashboardFolder: Dashboard2FolderT) => void;
  data?: Dashboard2ListT;
  refetch?: () => void;
  folderAlias: string | null;
}) => {
  const { data: allDashboardData } = useDashboardTreeListQuery(null);
  const { mutate: updateDashboardStarred } = useDashboardStarMutation({
    onSuccess() {
      refetch?.();
    },
  });
  const wr = useWorkspaceRouter();

  const toDetail = (alias: string, type: 'dashboard' | 'folder') => {
    if (type === 'dashboard') {
      wr.push(`/dashboard2/${alias}`);
    }

    if (type === 'folder') {
      wr.push(`/dashboard2/list?folder_alias=${alias}`);
    }
  };

  const upperFolderRowData = folderAlias
    ? {
        rowKey: folderAlias,
        rowData: [
          {
            columnKey: 'title',
            children: (
              <Flex a="center" g={10}>
                <Flex style={{ fontSize: 16 }}>
                  <FolderOutlined />
                </Flex>
                <ContentTitle>..</ContentTitle>
              </Flex>
            ),
            rawData: 'Upper folder',
            onCellClick: () => wr.push(`/dashboard2/list`),
          },
        ],
        visible: false,
      }
    : null;

  const folderData = data?.dashboard_folders?.map((folder) => {
    return {
      rowKey: folder.alias,
      rowData: [
        {
          columnKey: 'title',
          children: (
            <Flex a="center" g={10}>
              <Flex style={{ fontSize: 16 }}>
                <FolderOutlined />
              </Flex>
              <ContentTitle>{folder.name}</ContentTitle>
            </Flex>
          ),
          rawData: folder.name,
          onCellClick: () => toDetail(folder.alias, 'folder'),
        },
        {
          columnKey: 'last_updated',
          children: getTimezoneDayjs(folder.updated_at).fromNow(),
          rawData: folder.updated_at,
        },
        {
          columnKey: 'modifed_by',
          children: (
            <ProfileAvatar type={1} text={folder.updated_by} withText />
          ),
          rawData: folder.updated_by,
        },
        {
          columnKey: 'actions',
          children: (
            <DashboardFolderAction
              folder={folder}
              searchValue={searchValue}
              onEditDashboardFolder={onEditDashboardFolder}
            />
          ),
          rawData: 0,
        },
      ],
      visible: false,
    };
  }, []);

  const dashboardData = data?.dashboards?.map((dashboard) => {
    return {
      rowKey: dashboard.alias,
      rowData: [
        {
          columnKey: 'title',
          children: (
            <VFlex g={6}>
              <Flex a="center" g={10}>
                <CenterFlex>
                  <DashboardIcon style={{ fontSize: 16 }} />
                </CenterFlex>
                <ContentTitle>{dashboard.title}</ContentTitle>
                <FavoriteToggle
                  value={!!dashboard.starred_at}
                  onChange={(value) => {
                    updateDashboardStarred({
                      dashboardAlias: dashboard.alias,
                      starred: value,
                    });
                  }}
                />
              </Flex>

              {dashboard.path && (
                <Flex a="center" g={6}>
                  <FolderOutlined />
                  <Typo className="text-gray-600" type="label3" weight="m">
                    {dashboard.path}
                  </Typo>
                </Flex>
              )}
            </VFlex>
          ),
          rawData: dashboard.title,
          onCellClick: () => toDetail(dashboard.alias, 'dashboard'),
        },
        {
          columnKey: 'last_updated',
          children: getTimezoneDayjs(dashboard.updated_at).fromNow(),
          rawData: dashboard.updated_at,
        },
        {
          columnKey: 'modifed_by',
          children: (
            <ProfileAvatar type={1} text={dashboard.updated_by} withText />
          ),
          rawData: dashboard.updated_by,
        },
        {
          columnKey: 'actions',
          children: (
            <Dashboard2Action
              dashboard={dashboard}
              searchValue={searchValue}
              onEditDashboard={onEditDashboard}
              refetch={refetch}
              folders={allDashboardData?.dashboard_folders}
            />
          ),
          rawData: 0,
        },
      ],
    };
  }, []);

  const tableData = [
    ...(upperFolderRowData ? [upperFolderRowData] : []),
    ...(folderData ?? []),
    ...(dashboardData ?? []),
  ];

  return {
    tableData,
    columns,
  };
};

export default useDashboard2ListTable;
